import type { IGetAllLeagues } from '../components/header/types/header';
import { getPlayerImageUrl, resolvePublicImageUrl } from '../helper/helper';

export type GlobalSearchKind = 'league' | 'tournament' | 'team' | 'player';

export type GlobalSearchItem = {
  id: string;
  type: GlobalSearchKind;
  title: string;
  subtitle?: string;
  leagueId?: string;
  leagueName?: string;
  tournamentId?: string;
  tournamentName?: string;
  teamId?: string;
  teamSnapshotId?: string;
  playerId?: string;
  playerName?: string;
  /** Player photo URL (CDN-resolved); undefined when the player has none */
  imageUrl?: string;
  playerCode?: string;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const asArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value
        .map((item) => asRecord(item))
        .filter((item): item is Record<string, unknown> => item != null)
    : [];

const toStringValue = (value: unknown): string | undefined => {
  if (value == null) return undefined;
  const trimmed = String(value).trim();
  return trimmed || undefined;
};

const SHORT_DATE: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' };
const shortDate = (value: unknown): string | undefined => {
  const raw = toStringValue(value);
  if (!raw) return undefined;
  const d = new Date(/^\d+$/.test(raw) ? Number(raw) : raw);
  return Number.isNaN(d.getTime()) ? undefined : d.toLocaleDateString('en-GB', SHORT_DATE);
};
const FORMAT_LABELS: Record<string, string> = { TEST_MATCH: 'Multi Day', THE_HUNDRED: 'The Hundred', LIMITED_OVERS: 'Limited Overs' };
const formatName = (v?: string) => (v ? FORMAT_LABELS[v] ?? v.replace(/_/g, ' ') : undefined);

const leagueSubtitle = (item: Record<string, unknown>): string => {
  const place = [item.city, item.state, item.country].map(toStringValue).filter(Boolean).slice(0, 2).join(', ');
  const count = Array.isArray(item.tournamentIds) ? item.tournamentIds.length : 0;
  const parts = [place, count ? `${count} tournament${count === 1 ? '' : 's'}` : ''].filter(Boolean);
  return parts.join(' · ') || 'League';
};
const tournamentSubtitle = (item: Record<string, unknown>): string => {
  const start = shortDate(item.startDate), end = shortDate(item.endDate);
  const when = start && end && start !== end ? `${start} – ${end}` : start;
  return [toStringValue(item.leagueName), formatName(toStringValue(item.matchType)), when].filter(Boolean).join(' · ') || 'Tournament';
};

const buildPlayerName = (item: Record<string, unknown>): string | undefined => {
  const directName = toStringValue(item.playerName ?? item.name);
  if (directName) return directName;

  const firstName = toStringValue(item.playerFirstName ?? item.firstName);
  const lastName = toStringValue(item.playerLastName ?? item.lastName);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || undefined;
};

const pushUniqueSearchItem = (
  list: GlobalSearchItem[],
  seen: Set<string>,
  item: GlobalSearchItem | null,
) => {
  if (!item) return;
  const key = `${item.type}:${item.id}`;
  if (seen.has(key)) return;
  seen.add(key);
  list.push(item);
};

/** Parse `websearch` API payload into normalized items (same as TopHeader / GlobalSearchBar). */
export function extractGlobalSearchItems(payload: unknown): GlobalSearchItem[] {
  const root = asRecord(payload);
  const groups =
    root?.data && asRecord(root.data) ? (root.data as Record<string, unknown>) : root;

  const results: GlobalSearchItem[] = [];
  const seen = new Set<string>();

  const leagueArrays: Record<string, unknown>[] = [
    ...asArray(groups?.leagueResponses),
    ...asArray(groups?.leagueResponseList),
    ...asArray(groups?.leagues),
  ];
  const singleLeagueResponse = asRecord(groups?.leagueResponse);
  if (singleLeagueResponse) leagueArrays.push(singleLeagueResponse);

  leagueArrays.forEach((item) => {
    const leagueId = toStringValue(
      item.leagueId ?? item.id ?? item.leagueID ?? item.league_id,
    );
    const leagueName = toStringValue(
      item.leagueName ?? item.name ?? item.league_name ?? item.title ?? item.leagueTitle,
    );
    if (!leagueId || !leagueName) return;
    pushUniqueSearchItem(results, seen, {
      id: leagueId,
      type: 'league',
      title: leagueName,
      subtitle: leagueSubtitle(item),
      imageUrl: resolvePublicImageUrl(toStringValue(item.imageUri ?? item.logoUri)) || undefined,
      leagueId,
      leagueName,
    });
  });

  const tournamentArrays = [
    ...asArray(groups?.tournamentResponses),
    ...asArray(groups?.tournaments),
    ...asArray(groups?.tournamentResponseList),
  ];

  tournamentArrays.forEach((item) => {
    const tournamentId = toStringValue(item.tournamentId ?? item.id);
    const tournamentName = toStringValue(item.tournamentName ?? item.name);
    if (!tournamentId || !tournamentName) return;
    pushUniqueSearchItem(results, seen, {
      id: tournamentId,
      type: 'tournament',
      title: tournamentName,
      subtitle: tournamentSubtitle(item),
      imageUrl: resolvePublicImageUrl(toStringValue(item.logoUri ?? item.imageUri)) || undefined,
      leagueId: toStringValue(item.leagueId),
      leagueName: toStringValue(item.leagueName),
      tournamentId,
      tournamentName,
    });
  });

  const teamArrays = [
    ...asArray(groups?.teamResponses),
    ...asArray(groups?.teams),
    ...asArray(groups?.teamResponseList),
  ];

  teamArrays.forEach((item) => {
    const teamId = toStringValue(item.teamId ?? item.id);
    const teamName = toStringValue(item.teamName ?? item.name);
    if (!teamId || !teamName) return;
    pushUniqueSearchItem(results, seen, {
      id: teamId,
      type: 'team',
      title: teamName,
      subtitle: toStringValue(item.tournamentName ?? item.leagueName) ?? 'Team',
      leagueId: toStringValue(item.leagueId),
      leagueName: toStringValue(item.leagueName),
      tournamentId: toStringValue(item.tournamentId),
      tournamentName: toStringValue(item.tournamentName),
      teamId,
      teamSnapshotId: toStringValue(item.teamSnapshotId),
    });
  });

  const playerArrays = [
    ...asArray(groups?.playerResponses),
    ...asArray(groups?.playerProfileResponses),
    ...asArray(groups?.players),
    ...asArray(groups?.playerResponseList),
    ...asArray(groups?.playerProfileResponseList),
    ...asArray(groups?.playerProfiles),
  ];

  playerArrays.forEach((item) => {
    const playerId = toStringValue(
      item.playerId ??
        item.id ??
        item.playerID ??
        item.player_id ??
        item.profileId ??
        item.playerProfileId,
    );
    const playerName = buildPlayerName(item);
    if (!playerId || !playerName) return;
    pushUniqueSearchItem(results, seen, {
      id: playerId,
      type: 'player',
      title: playerName,
      subtitle:
        toStringValue(
          item.teamName ?? item.leagueName ?? item.tournamentName ?? item.playerRole,
        ) ?? 'Player',
      leagueId: toStringValue(item.leagueId),
      leagueName: toStringValue(item.leagueName),
      tournamentId: toStringValue(item.tournamentId),
      tournamentName: toStringValue(item.tournamentName),
      teamId: toStringValue(item.teamId),
      playerId,
      playerName,
      imageUrl: getPlayerImageUrl(toStringValue(item.imageUri ?? item.imageUrl ?? item.profileImageUri)) || undefined,
      playerCode: toStringValue(item.playerCode),
    });
  });

  if (results.length === 0 && Array.isArray(payload)) {
    asArray(payload).forEach((item) => {
      const tournamentId = toStringValue(item.tournamentId);
      const tournamentName = toStringValue(item.tournamentName);
      const leagueId = toStringValue(item.leagueId);
      const leagueName = toStringValue(item.leagueName);

      if (tournamentId && tournamentName) {
        pushUniqueSearchItem(results, seen, {
          id: tournamentId,
          type: 'tournament',
          title: tournamentName,
          subtitle: leagueName ?? 'Tournament',
          leagueId,
          leagueName,
          tournamentId,
          tournamentName,
        });
        return;
      }

      const lid = leagueId ?? toStringValue(item.leagueID ?? item.league_id ?? item.id);
      const lname =
        leagueName ??
        toStringValue(item.league_name ?? item.title ?? item.leagueTitle ?? item.name);
      if (lid && lname) {
        pushUniqueSearchItem(results, seen, {
          id: lid,
          type: 'league',
          title: lname,
          subtitle: 'League',
          leagueId: lid,
          leagueName: lname,
        });
      }
    });
  }

  return results;
}

/** Minimal league row when websearch returns a league not in getAllLeagues. */
export function buildMinimalLeagueFromWebSearch(
  leagueId: string,
  leagueName: string,
): IGetAllLeagues {
  return {
    leagueId,
    leagueName,
    organizerName: '',
    organizerEmailId: '',
    organizer: null,
    organizerPhoneNumber: '',
    imageUri: '',
    latestTournamentId: '',
    tournamentIds: [],
    roleName: '',
    city: '',
    state: '',
    country: '',
    bannerUri: '',
    association: '',
    countryCode: 0,
    canEditLeague: false,
    canAddTournamentToLeague: false,
    canAddTeamToLeague: false,
    canAddPlayerToTeam: false,
    canCreateDivisions: false,
    umpireLeagueStatus: null,
    groundsRegistered: 0,
    sponsorsRegistered: 0,
    allowPlayersToJoin: false,
    umpire: false,
  };
}

/** Prefer full league from allLeagues (permissions) when ids match. */
export function mergeWebSearchLeagueIntoList(
  item: GlobalSearchItem,
  allLeagues: IGetAllLeagues[],
): IGetAllLeagues {
  const id = item.leagueId ?? item.id;
  const name = item.leagueName ?? item.title;
  const existing = allLeagues.find((l) => l.leagueId === id);
  return existing ?? buildMinimalLeagueFromWebSearch(id, name);
}
